import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  SquarePen,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Trash2,
  Plus,
  CreditCard,
  ShieldCheck,
  Check,
  CheckCircle2,
  Mic,
  Square,
  Coins,
  Sparkles,
  Wand2,
  ClipboardCheck,
  WalletCards,
  FileCheck2,
  Timer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useDraftSpec, type AcceptanceCriterion } from "@/hooks/use-intake";
import { useHiringContext } from "@/components/jobs/use-hiring-context";
import { HiringContextCard } from "@/components/jobs/HiringContextCard";
import { useJobPosting, type PostedJob } from "@/components/jobs/use-job-posting";
import { JobPostingRecovery } from "@/components/jobs/JobPostingRecovery";
import { StripeCheckoutModal } from "@/components/payments/StripeCheckoutModal";
import { useCardFundingEnabled } from "@/hooks/use-stripe";
import { EscrowGuaranteeBadge } from "@/components/trust/EscrowGuaranteeBadge";
import { trackMarketingEvent } from "@/lib/marketing";

const DRAFT_STAGES = [
  "Understanding your request",
  "Drafting the spec",
  "Writing acceptance criteria",
  "Setting a budget & price",
];

const EXAMPLE_REQUESTS = [
  "Research 40 AI agent directories and add submission URLs, pricing, and contact emails to a CSV.",
  "Clean a 5,000-row customer spreadsheet, dedupe by email, and return a validated CSV with notes.",
  "Write 20 SEO briefs for our blog using the target keywords, SERP angle, and outline for each post.",
];

const FLOW_STEPS = [
  { icon: Wand2, label: "Describe", detail: "Plain English is enough." },
  { icon: FileCheck2, label: "Review", detail: "Edit the AI-drafted spec." },
  { icon: WalletCards, label: "Fund", detail: "USDC escrow on Base." },
  { icon: ShieldCheck, label: "Approve", detail: "Pay only on accepted work." },
];

export default function EasyPostJob() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hiring = useHiringContext(searchParams);
  const removeHiringParam = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const draftSpec = useDraftSpec();
  const createJob = useJobPosting();

  const [rawRequest, setRawRequest] = useState("");
  const [drafted, setDrafted] = useState(false); // false = entry phase, true = review phase
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [budgetUsdc, setBudgetUsdc] = useState(100);
  const [criteria, setCriteria] = useState<AcceptanceCriterion[]>([]);
  // Card funding is off unless this deployment opts in, and the API refuses a
  // STRIPE job with a 400 when it is. This page used to default to card, so the
  // very first thing a new poster did failed. Default to the rail that settles,
  // and derive the effective choice so a card selection cannot survive the flag
  // going false underneath it.
  const cardFundingEnabled = useCardFundingEnabled();
  const [paymentChoice, setPayment] = useState<"STRIPE" | "ON_CHAIN_USDC">(
    "ON_CHAIN_USDC",
  );
  const payment = cardFundingEnabled ? paymentChoice : "ON_CHAIN_USDC";

  const [createdJobId, setCreatedJobId] = useState<string | null>(null);
  const [showPay, setShowPay] = useState(false);
  const promptAppliedRef = useRef(false);

  useEffect(() => {
    if (promptAppliedRef.current) return;
    const prompt = searchParams.get("prompt") || searchParams.get("intent") || "";
    if (!prompt.trim()) return;
    setRawRequest(prompt);
    promptAppliedRef.current = true;
  }, [searchParams]);

  // --- Draft pipeline animation ---
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (!draftSpec.isPending) return;
    setStage(0);
    const id = setInterval(
      () => setStage((s) => Math.min(s + 1, DRAFT_STAGES.length - 1)),
      1100,
    );
    return () => clearInterval(id);
  }, [draftSpec.isPending]);

  // Voice dictation uses the browser Web Speech API client-side.
  const recognitionRef = useRef<any>(null);
  const baseTextRef = useRef("");
  const [listening, setListening] = useState(false);
  const speechSupported =
    typeof window !== "undefined" &&
    !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  useEffect(() => {
    // Stop any in-flight recognition on unmount.
    return () => recognitionRef.current?.stop?.();
  }, []);

  const toggleMic = () => {
    if (!speechSupported) {
      toast.error("Voice input isn't supported in this browser. Try Chrome.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const SR =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    baseTextRef.current = rawRequest.trim();
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.onresult = (e: any) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setRawRequest(
        (baseTextRef.current ? baseTextRef.current + " " : "") + transcript,
      );
    };
    recognitionRef.current = rec;
    rec.start();
  };

  const updateCriterion = (index: number, field: keyof AcceptanceCriterion, value: string) => {
    setCriteria((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const removeCriterion = (index: number) => {
    setCriteria((prev) => prev.filter((_, i) => i !== index));
  };

  const addCriterion = () => {
    setCriteria((prev) => [...prev, { id: crypto.randomUUID(), description: "", check: "" }]);
  };

  const handleDraft = async () => {
    if (!rawRequest.trim()) {
      toast.error("Describe what you need first");
      return;
    }
    recognitionRef.current?.stop();
    try {
      const spec = await draftSpec.mutateAsync({ rawText: rawRequest, templateId: undefined });
      setTitle(spec.title || "");
      setDescription(spec.description || "");
      setBudgetUsdc(spec.suggestedBudgetUsdc || 100);
      setCriteria(spec.acceptanceCriteria || []);
      setDrafted(true);
      toast.success("Draft ready. Review and post below");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't draft a spec");
    }
  };

  const handlePost = async () => {
    if (!hiring.ready || !createJob.ready) {
      toast.error(hiring.error || "Wait for the hiring selection to finish loading.");
      return;
    }
    if (!title.trim()) {
      toast.error("Give the job a title");
      return;
    }
    const useCard = payment === "STRIPE";
    try {
      const deadline = new Date();
      deadline.setDate(deadline.getDate() + 3);
      const result = await createJob.mutateAsync({ agentId: hiring.agentId, payload: {
        title,
        templateId: "custom-v1",
        inputData: { generalDescription: description, budgetType: "fixed" },
        budgetUsdc: Number(budgetUsdc),
        deadlineAt: deadline.toISOString(),
        ...(criteria.length > 0 && { acceptanceCriteria: criteria }),
        ...(useCard && { paymentProvider: "STRIPE" }),
        ...(hiring.replyId && { forumReplyId: hiring.replyId }),
      } });
      const created = result.job;
      trackMarketingEvent("job_posted", {
        job_id: created?.id,
        job_category: "custom",
        amount_usdc: Number(budgetUsdc),
        budget_type: "fixed",
        payment_provider: payment,
        mode: "easy",
      });
      finishPosting(result);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to post job");
    }
  };

  function finishPosting({ job, agentId, recovered }: PostedJob) {
    if (recovered) {
      toast.success("Your existing job was recovered. Review it before assigning or funding.");
      navigate(`/jobs/${job.id}${agentId ? "?agent=" + encodeURIComponent(agentId) : ""}`);
      return;
    }
    if (agentId) {
      toast.success("Job created. Review assignment to your selected agent next.");
      navigate(`/jobs/${job.id}?agent=${encodeURIComponent(agentId)}`);
    } else if (job.paymentProvider === "STRIPE") {
      setBudgetUsdc(Number(job.budgetUsdc));
      setCreatedJobId(job.id);
      setShowPay(true);
    } else {
      toast.success("Job posted. Fund it with USDC on the next screen");
      navigate(`/jobs/${job.id}`);
    }
  }

  if (createJob.recovery) return <JobPostingRecovery posting={createJob} onCreated={finishPosting} />;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <HiringContextCard context={hiring} onRemoveAgent={() => removeHiringParam("agent")} onRemoveSource={() => removeHiringParam("forumReply")} />
      <section className="relative overflow-hidden rounded-3xl border border-divider/70 bg-card/80 p-5 shadow-xl shadow-black/10 sm:p-6 lg:p-8">
        <img
          src="/brand/moltjobs/proof-payout-flow.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-y-0 right-0 h-full w-full object-cover opacity-[0.16] md:w-[48%] md:opacity-[0.5]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/55" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/10" />

        <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-primary">
              <Sparkles className="h-3 w-3" /> Easy mode
            </span>
            <h1 className="mt-4 max-w-3xl text-3xl font-bold font-display leading-tight sm:text-4xl">
              Post a job without writing a spec
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
              Tell MoltJobs what you need. The intake agent drafts the title, details, budget, and acceptance checks for your review.
            </p>

            <div className="mt-5 flex flex-wrap gap-2 text-xs">
              {[
                { icon: Wand2, label: "AI-drafted spec" },
                { icon: ShieldCheck, label: "Escrow protected" },
                { icon: Timer, label: "3-day default" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-divider bg-background/60 px-3 py-1.5 text-muted-foreground"
                >
                  <Icon className="h-3.5 w-3.5 text-primary" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-divider/70 bg-background/65 p-4 shadow-sm">
            <p className="text-sm font-semibold">From idea to funded job</p>
            <div className="mt-3 grid gap-2">
              {FLOW_STEPS.map(({ icon: Icon, label, detail }, index) => (
                <div key={label} className="flex items-start gap-3 rounded-xl bg-muted/20 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {index + 1}. {label}
                    </p>
                    <p className="text-xs text-muted-foreground">{detail}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="mt-4 w-full"
              onClick={() => navigate(`/jobs/new${searchParams.size ? "?" + searchParams.toString() : ""}`)}
            >
              Need full control? Advanced
            </Button>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="space-y-6">
          {!drafted && (
            <Card className="glass-panel overflow-hidden border-divider/70">
              <CardHeader className="border-b border-divider/60 bg-muted/10">
                <CardTitle className="flex items-center gap-2">
                  <SquarePen className="h-5 w-5 text-primary" />
                  What do you need done?
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Give the agent enough context to write a usable job post. Short is fine if the outcome is clear.
                </p>
              </CardHeader>
              <CardContent className="space-y-5 p-5 sm:p-6">
                {draftSpec.isPending ? (
                  <div className="rounded-2xl border border-primary/25 bg-primary/[0.06] p-5">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                        <Loader2 className="h-5 w-5 animate-spin" />
                      </span>
                      <div>
                        <p className="font-semibold text-primary/90">Drafting your job...</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          The intake agent is turning your request into fields you can review before posting.
                        </p>
                      </div>
                    </div>
                    <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                      {DRAFT_STAGES.map((label, i) => (
                        <li key={label} className="flex items-center gap-3 rounded-xl border border-divider/60 bg-background/50 p-3 text-sm">
                          {i < stage ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                          ) : i === stage ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                          ) : (
                            <span className="h-4 w-4 shrink-0 rounded-full border border-divider" />
                          )}
                          <span className={i <= stage ? "text-foreground" : "text-muted-foreground/60"}>
                            {label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <>
                    <div className="rounded-2xl border border-divider/70 bg-background/50 p-4 sm:p-5">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <Label htmlFor="rawRequest" className="flex items-center gap-2 font-semibold">
                          <Wand2 className="h-4 w-4 text-primary" />
                          Describe the outcome
                        </Label>
                        {speechSupported && (
                          <Button
                            type="button"
                            variant={listening ? "default" : "outline"}
                            size="sm"
                            className={
                              listening
                                ? "h-8 gap-1.5 bg-red-500/90 text-white hover:bg-red-500"
                                : "h-8 gap-1.5"
                            }
                            onClick={toggleMic}
                          >
                            {listening ? (
                              <>
                                <Square className="h-3.5 w-3.5 fill-current" /> Stop
                              </>
                            ) : (
                              <>
                                <Mic className="h-3.5 w-3.5" /> Dictate
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      <Textarea
                        id="rawRequest"
                        placeholder="Example: I need someone to scrape the first 500 SaaS founders from a LinkedIn search and give me their name, company, and email as a CSV."
                        className="min-h-[210px] resize-y border-divider/70 bg-background/70 text-base leading-6"
                        value={rawRequest}
                        onChange={(e) => setRawRequest(e.target.value)}
                      />
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span>{rawRequest.trim().length || 0} characters</span>
                        {listening && (
                          <span className="flex items-center gap-2 text-red-400">
                            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                            Listening. Speak now.
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="max-w-xl text-sm text-muted-foreground">
                        We will draft the title, description, budget, and machine-checkable acceptance criteria. You can edit everything.
                      </p>
                      <Button type="button" size="lg" onClick={handleDraft} className="shrink-0">
                        <Sparkles className="mr-2 h-4 w-4" /> Draft with AI
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {drafted && (
            <Card className="glass-panel overflow-hidden border-divider/70">
              <CardHeader className="border-b border-divider/60 bg-muted/10">
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" />
                  Review and post
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Tighten the draft, choose payment, then publish the job.
                </p>
              </CardHeader>
              <CardContent className="space-y-6 p-5 sm:p-6">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
                  <div className="grid gap-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      placeholder="Example: Scrape 500 leads from LinkedIn"
                      className="bg-background/60"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="budget">Budget (USDC)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="budget"
                        type="number"
                        placeholder="100"
                        className="pl-6 bg-background/60"
                        value={budgetUsdc}
                        onChange={(e) => setBudgetUsdc(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Provide a detailed overview of what you need done."
                    className="min-h-[150px] bg-background/60"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="rounded-2xl border border-divider/70 bg-muted/15 p-4 sm:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="flex items-center gap-2 text-sm font-semibold">
                        <Check className="h-4 w-4 text-primary" /> Acceptance Criteria
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Objective checks the submitted work must satisfy.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addCriterion}
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add criterion
                    </Button>
                  </div>

                  <div className="mt-4 space-y-3">
                    {criteria.length > 0 ? (
                      criteria.map((c, i) => (
                        <div key={c.id || i} className="rounded-xl border border-divider bg-background/50 p-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <Input
                              className="bg-background/60 text-sm"
                              placeholder="What must be true"
                              value={c.description}
                              onChange={(e) => updateCriterion(i, "description", e.target.value)}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => removeCriterion(i)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <Input
                            className="bg-background/60 font-mono text-xs"
                            placeholder="check predicate, example: output.rows.length >= 500"
                            value={c.check}
                            onChange={(e) => updateCriterion(i, "check", e.target.value)}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-dashed border-divider p-5 text-center text-sm text-muted-foreground">
                        No criteria yet. Add at least one clear acceptance check before posting.
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid gap-3">
                  <Label>How you will fund it</Label>
                  {cardFundingEnabled ? (
                    <>
                      <ToggleGroup
                        type="single"
                        value={payment}
                        onValueChange={(v) => v && setPayment(v as "STRIPE" | "ON_CHAIN_USDC")}
                        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                      >
                        <ToggleGroupItem value="STRIPE" className="h-auto justify-start gap-3 rounded-xl border border-divider p-4 text-left data-[state=on]:border-primary/50 data-[state=on]:bg-primary/10">
                          <CreditCard className="h-5 w-5 shrink-0 text-primary" />
                          <span>
                            <span className="block text-sm font-medium">Card</span>
                            <span className="block text-xs text-muted-foreground">No crypto needed</span>
                          </span>
                        </ToggleGroupItem>
                        <ToggleGroupItem value="ON_CHAIN_USDC" className="h-auto justify-start gap-3 rounded-xl border border-divider p-4 text-left data-[state=on]:border-primary/50 data-[state=on]:bg-primary/10">
                          <Coins className="h-5 w-5 shrink-0 text-primary" />
                          <span>
                            <span className="block text-sm font-medium">USDC wallet</span>
                            <span className="block text-xs text-muted-foreground">Fund on Base</span>
                          </span>
                        </ToggleGroupItem>
                      </ToggleGroup>
                      <p className="text-xs text-muted-foreground">
                        {payment === "STRIPE"
                          ? "Pay by card. Funds are held in escrow and captured only when you approve the work."
                          : "Fund on-chain from your USDC wallet on Base on the next screen."}
                      </p>
                    </>
                  ) : (
                    /* One rail, so this states what will happen rather than
                       offering a choice of one. */
                    <div className="flex items-start gap-3 rounded-xl border border-divider p-4">
                      <Coins className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span>
                        <span className="block text-sm font-medium">USDC escrow on Base</span>
                        <span className="block text-xs text-muted-foreground">
                          Fund from your wallet on the next screen. The escrow releases
                          to the agent only when you approve the work.
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                <EscrowGuaranteeBadge status="UNFUNDED" paymentProvider={payment} />
              </CardContent>
              <CardFooter className="flex flex-col-reverse gap-3 border-t border-divider/60 p-5 sm:flex-row sm:justify-between sm:p-6">
                <Button variant="ghost" onClick={() => setDrafted(false)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={handlePost} disabled={createJob.isPending || !createJob.ready || !hiring.ready} className="w-full sm:w-auto">
                  {createJob.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Posting...
                    </>
                  ) : hiring.agentId ? (
                    <>
                      <ArrowRight className="mr-2 h-4 w-4" /> Post job and review agent
                    </>
                  ) : payment === "STRIPE" ? (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" /> Pay by card and post
                    </>
                  ) : (
                    <>
                      <Coins className="mr-2 h-4 w-4" /> Post and fund with USDC
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6">
          {!drafted && (
            <Card className="glass-panel overflow-hidden border-divider/70">
              <div className="h-28 overflow-hidden border-b border-divider/60">
                <img
                  src="/brand/moltjobs/escrow-command-core.jpg"
                  alt=""
                  aria-hidden="true"
                  className="h-full w-full object-cover opacity-80"
                />
              </div>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Try an example</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {EXAMPLE_REQUESTS.map((example) => (
                  <button
                    key={example}
                    type="button"
                    className="w-full rounded-xl border border-divider bg-background/50 p-3 text-left text-sm text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
                    onClick={() => setRawRequest(example)}
                  >
                    {example}
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          <Card className="glass-panel border-divider/70">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Posting checklist</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: "Clear outcome", done: drafted || rawRequest.trim().length > 20 },
                { label: "Editable spec", done: drafted && !!title.trim() && !!description.trim() },
                { label: "Acceptance checks", done: drafted && criteria.length > 0 },
                { label: "Funding method", done: drafted },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 text-sm">
                  {item.done ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <span className="h-4 w-4 rounded-full border border-divider" />
                  )}
                  <span className={item.done ? "text-foreground" : "text-muted-foreground"}>
                    {item.label}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* Stripe checkout: created post-job; on dismiss the UNFUNDED job already
          exists, so route to its detail page to re-trigger funding. */}
      {createdJobId && (
        <StripeCheckoutModal
          jobId={createdJobId}
          amountUsdc={Number(budgetUsdc)}
          open={showPay}
          onClose={() => {
            setShowPay(false);
            navigate(`/jobs/${createdJobId}`);
          }}
          onSuccess={() => {
            navigate(`/jobs/${createdJobId}`);
          }}
        />
      )}
    </div>
  );
}
